import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, Button, Box, Typography, CircularProgress } from '@mui/material';
import { Html5Qrcode } from 'html5-qrcode';

const SCANNER_DIV_ID = 'qr-reader';

export interface QrDispersalPayload {
  code: string;
  gate: string;
}

export function parseQrDispersalPayload(decodedText: string): QrDispersalPayload {
  const t = (decodedText || '').trim();
  if (!t) return { code: '', gate: '' };

  try {
    if (t.startsWith('http://') || t.startsWith('https://')) {
      const url = new URL(t);
      const code = url.searchParams.get('code') || url.searchParams.get('schoolCode') || '';
      const gate = url.searchParams.get('gate') || url.searchParams.get('gateId') || '';
      return { code: code.toUpperCase(), gate };
    }
    if ((t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))) {
      const obj = JSON.parse(t) as { code?: string; schoolCode?: string; gate?: string; gateId?: string };
      const code = (obj.code || obj.schoolCode || '').toString().toUpperCase();
      const gate = (obj.gate || obj.gateId || '').toString();
      return { code, gate };
    }
    const sep = t.includes('|') ? '|' : t.includes(',') ? ',' : null;
    if (sep) {
      const parts = t.split(sep).map((s) => s.trim());
      return {
        code: (parts[0] || '').toUpperCase(),
        gate: parts[1] || '',
      };
    }
    return { code: t.toUpperCase(), gate: '' };
  } catch {
    return { code: t.toUpperCase(), gate: '' };
  }
}

interface QrScannerDialogProps {
  open: boolean;
  onClose: () => void;
  onScan: (payload: QrDispersalPayload) => void;
}

type FacingMode = 'environment' | 'user';

const QrScannerDialog = ({ open, onClose, onScan }: QrScannerDialogProps) => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [facingMode, setFacingMode] = useState<FacingMode>('environment');
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const startedRef = useRef(false);
  const stopPromiseRef = useRef<Promise<void>>(Promise.resolve());

  const handleRetry = () => {
    setError('');
    setLoading(true);
    setRetryCount((c) => c + 1);
  };

  const handleSwitchCamera = () => {
    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
    setLoading(true);
  };

  useEffect(() => {
    if (!open) return;

    setError('');
    setLoading(true);
    startedRef.current = false;

    const startScanner = async () => {
      await stopPromiseRef.current;
      const el = document.getElementById(SCANNER_DIV_ID);
      if (el) el.innerHTML = '';
      try {
        const cameras = await Html5Qrcode.getCameras();
        if (!cameras || cameras.length === 0) {
          setError('No camera found. Use manual entry.');
          setLoading(false);
          return;
        }
        const scanner = new Html5Qrcode(SCANNER_DIV_ID);
        scannerRef.current = scanner;
        const cameraConfig = { facingMode };

        await scanner.start(
          cameraConfig,
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
          },
          (decodedText: string) => {
            if (!startedRef.current) return;
            startedRef.current = false;
            const { code, gate } = parseQrDispersalPayload(decodedText);
            scannerRef.current = null;
            scanner.stop().catch(() => {});
            onScan({ code, gate });
            onClose();
          },
          () => {}
        );
        startedRef.current = true;
        setLoading(false);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (/Permission dismissed|NotAllowedError|NotAllowed/i.test(msg)) {
          setError('Camera access was denied or dismissed. Please allow camera when prompted, or in site settings, then try again.');
        } else if (/NotFoundError|no camera/i.test(msg)) {
          setError('No camera found. Use manual entry.');
        } else {
          setError(msg || 'Could not start camera. Use manual entry.');
        }
        setLoading(false);
      }
    };

    startScanner();

    return () => {
      startedRef.current = false;
      const scanner = scannerRef.current;
      scannerRef.current = null;
      if (scanner) {
        stopPromiseRef.current = scanner
          .stop()
          .then(() => {
            try {
              scanner.clear();
            } catch {
              // ignore
            }
          })
          .catch(() => {});
      }
    };
  }, [open, onClose, onScan, retryCount, facingMode]);

  const handleClose = async () => {
    if (closing) return;
    setClosing(true);
    const scanner = scannerRef.current;
    scannerRef.current = null;
    startedRef.current = false;
    if (scanner) {
      try {
        await scanner.stop();
        try {
          scanner.clear();
        } catch {
          // ignore
        }
      } catch {
        // ignore
      }
    }
    setClosing(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Scan gate QR code</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Point your camera at the QR code displayed at the school gate.
        </Typography>
        {error && (
          <>
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Button variant="outlined" onClick={handleRetry} sx={{ mb: 2 }}>
              Try again
            </Button>
          </>
        )}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        <Box id={SCANNER_DIV_ID} sx={{ minHeight: 200, display: loading ? 'none' : 'block' }} />
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleSwitchCamera}
            disabled={loading || !!error}
          >
            {facingMode === 'environment' ? 'Front camera' : 'Back camera'}
          </Button>
          <Button onClick={handleClose} disabled={closing}>
            {closing ? 'Closing…' : 'Cancel'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default QrScannerDialog;
